// 기존 유저 이관 — 계정당 한 번, 서버 기준(docs/CHARACTER-PRD.md §13.5). 계정 클라이언트가 온라인·안전 장면에서 부른다(부팅은 이관하지 않는다).
//
// 순서(계정 저장 동기화가 끝나 이 기기 원래 키 = 계정 서버 원래 진행 정본인 상태에서):
//  1) 이 기기에 이관 표시가 있으면 끝(다시 이관하지 않는다 — 계정이 바뀌어도).
//  2) 서버 이관 상태 GET. 완료(done)면 "받기": 서버 캐릭터 목록 + 각 캐릭터 스냅샷을 내려받아 캐릭터 키에 쓰고 동기화 기준을 서버 rev로 맞춘다.
//     원래 키는 건드리지 않고 "처리됨(account, planId)" 표시만 남긴다.
//  3) 이 기기에 원래 진행이 없으면: 서버에 계획이 없으면 표시(none) + 받기, 진행 중이면 기다린다(busy).
//  4) 원래 진행이 있으면 첫 기기 후보: readLegacyState(검증 복구 + 미정산 보상 정산)로 계획을 만들어 claim.
//     - mine: 캐릭터 키 쓰기 → 캐릭터마다 등록(claim 동반) · 스냅샷 덮어쓰기 업로드 · 임대 갱신 → 완료(done) → 표시(first).
//       중간에 멈추면 같은 claim으로 처음부터 다시(서버는 같은 id·계획이면 멱등 덮어쓰기). 임대(10분)가 끝나면 다른 기기가 넘겨받아 처음부터 다시 한다.
//     - done(그 사이 다른 기기가 끝냄): 받기 · 아니면 busy(다음 동기화에 다시).
// 결과 'done'이면 호출자가 새로고침한다(부팅이 지금 캐릭터를 켠다).
import { EMPTY_SERVER_SNAPSHOT, SYNC_GEN, appliedMatches, collectSnapshot, snapshotHash, writeSnapshot, type KV } from '../account/keys';
import { TransportError, freshMeta, loadMeta, saveMeta, type Transport } from '../account/sync';
import type { Snapshot } from '../account/protocol';
import { CharacterStorage, MIGRATION_STAGING_PREFIX, charInstallingKey, charSyncKeys, isInstalling } from './keys';
import { planMigration, writeMigration } from './migrate';
import { parseAllocation } from './allocation';
import { MIGRATION_VERSION, freshRoster, fromView, loadRoster, saveRoster, type PendingMigration, type Roster } from './roster';
import { HERO_SELECT_KEY as HERO_KEY } from '../game/heroSelect';
import type { CharacterApi } from './api';

export type OnlineMigrationResult = 'done' | 'busy' | 'skip';
export interface OnlineMigrationDeps {
  /** 탭 사이 잠금(브라우저 navigator.locks). 없으면 이관하지 않는다 */
  locks: MigrationLocks | null;
  api: CharacterApi;
  transport: (characterId: string) => Transport;
  now?: () => number;
  randomHex?: (bytes: number) => string;
}

function hex(bytes: number): string {
  const a = new Uint8Array(bytes);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(a);
  else for (let i = 0; i < bytes; i++) a[i] = Math.floor(Math.random() * 256);
  return [...a].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** 원래 키(계정 동기화 진행 키)의 스냅샷 해시 — 처리 뒤 원래 키에 진행이 더 쌓였는지 비교용 */
export function legacyHashOf(kv: KV): string | null {
  const s = collectSnapshot(kv);
  return s && Object.keys(s.keys).length ? snapshotHash(s) : null;
}

/** 캐릭터 스냅샷을 서버 값으로 맞췄다고 그 캐릭터 동기화 메타에 적는다(다음 동기화가 같은 값을 다시 올리거나 내려받지 않게) */
function markSynced(kv: KV, id: string, accountId: string, rev: number, snap: Snapshot, now: number): boolean {
  const m = freshMeta();
  const hash = snapshotHash(snap);
  m.accountId = accountId; m.base = { accountId, rev, hash, g: SYNC_GEN }; m.lastSyncAt = now;
  saveMeta(kv, m, charSyncKeys(id).meta); // saveMeta는 저장 예외를 삼킨다 → 다시 읽어 실제로 저장됐는지 확인(Codex P2 #214)
  const got = loadMeta(kv, charSyncKeys(id).meta).base;
  return !!got && got.accountId === accountId && got.rev === rev && got.hash === hash;
}

/**
 * 원칙: 로컬에 설치하고 검증(다시 읽어 해시 비교)이 끝난 뒤에만 동기화 기준(sync base)을 적는다 — 이관·받기·hydrate 모두(Codex P1 #214).
 * install: 쓰기 함수(예외는 실패). 설치 뒤 캐릭터 저장소의 진행 스냅샷 해시가 expected와 같아야 기준을 적고 true. 아니면 기준을 적지 않고 false
 */
export function installVerified(kv: KV, id: string, accountId: string, rev: number, expected: Snapshot, install: () => void, now: number): boolean {
  // 원자성(Codex P1 #214): 설치 시작 표시를 먼저 저장 → 쓰기 → 검증 → 기준 기록 → 표시 삭제. 중간에 실패·탭 종료로 표시가 남으면
  // deriveRosterState가 무조건 받는 중으로 보고 업로드도 막는다(옛 기준 + 반쯤 바뀐 스냅샷을 "로컬 진행"으로 오인하지 않게)
  try { kv.setItem(charInstallingKey(id), String(rev)); if (kv.getItem(charInstallingKey(id)) !== String(rev)) return false; } catch { return false; }
  try { install(); } catch { return false; }
  let got: Snapshot | null;
  try { got = collectSnapshot(new CharacterStorage(kv, id)); } catch { return false; }
  // 서버 스냅샷이 모르는 세대의 키(옛 빌드가 올린 것)는 로컬 값이 남는다 — 그 키는 비교에서 뺀다(L1029, 다음 올리기가 서버에 되살린다)
  if (!got || !appliedMatches(got, expected)) return false;
  // 기준이 실제로 저장됐을 때만 표시를 지운다(메타 쓰기만 실패해도 표시가 남아 받는 중 — 옛 기준으로 새 서버 진행을 덮지 않게)
  try { if (!markSynced(kv, id, accountId, rev, expected, now)) return false; } catch { return false; }
  try { kv.removeItem(charInstallingKey(id)); } catch { return false; }
  return !isInstalling(kv, id);
}

/** 받기: 서버 캐릭터 전부를 내려받아 캐릭터 키에 쓴다. 원래 키는 그대로, 처리 표시(adopt|none)를 남긴다 */
async function adopt(kv: KV, roster: Roster, accountId: string, d: OnlineMigrationDeps, st: { allocation: unknown; hash: string | null }, mode: 'adopt' | 'none'): Promise<OnlineMigrationResult> {
  const now = (d.now ?? Date.now)();
  const list = await d.api.list();
  const live = list.characters.filter((c) => !c.deletedAt);
  const failed = new Set<string>();
  for (const c of live) {
    const s = await d.transport(c.id).getSave(false);
    const view = new CharacterStorage(kv, c.id);
    const data = s.data ?? EMPTY_SERVER_SNAPSHOT;
    // 설치·검증에 실패한 캐릭터는 기준 없이 「받는 중」으로 둔다(선택 불가 — 다음 목록 맞추기에서 다시 받는다)
    if (!installVerified(kv, c.id, accountId, s.rev, data, () => writeSnapshot(view, data), now)) failed.add(c.id);
  }
  const allocation = parseAllocation(st.allocation);
  const high = allocation ? allocation.ids[allocation.high] ?? null : null;
  const out: Roster = {
    ...roster,
    characters: list.characters.map((c) => fromView(c, accountId)), // 설치·검증 실패한 캐릭터는 기준이 없어 「받는 중」으로 파생된다
    current: high && live.some((c) => c.id === high && !failed.has(high)) ? high : live.find((c) => !failed.has(c.id))?.id ?? null,
    migrated: { v: MIGRATION_VERSION, at: now, mode, account: accountId, hash: st.hash, allocation, ids: [], higher: high, legacyHash: legacyHashOf(kv) },
  };
  delete out.pendingMigration;
  if (!saveRoster(kv, out)) throw new TransportError(0, 'storage');
  return 'done';
}

/** 이관 스냅샷 대기 키(claim별 불변): 업로드 원본은 공용 캐릭터 키가 아니라 여기 — 다른 탭이 캐릭터 키를 덮어도 업로드 원본은 그대로. 완료 뒤 캐릭터 키로 옮기고 지운다 */
export function stagingKey(claim: string, id: string): string { return MIGRATION_STAGING_PREFIX + claim + '.' + id; }
function readStaged(kv: KV, claim: string, id: string): Record<string, string> | null {
  try { const v: unknown = JSON.parse(kv.getItem(stagingKey(claim, id)) ?? 'null'); return v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, string> : null; } catch { return null; }
}
/** 대기 키 정리: 지금 claim 것을 뺀 나머지(다른 탭·옛 시도) — 열거할 수 있는 저장소에서만 */
function clearStaging(kv: KV, keep: string | null): void {
  const s = kv as unknown as { length?: unknown; key?: (i: number) => string | null };
  if (typeof s.length !== 'number' || typeof s.key !== 'function') return;
  const all: string[] = [];
  for (let i = 0; i < s.length; i++) { const k = s.key(i); if (k) all.push(k); }
  for (const k of all) if (k.startsWith(MIGRATION_STAGING_PREFIX) && (keep === null || !k.startsWith(MIGRATION_STAGING_PREFIX + keep + '.'))) kv.removeItem(k);
}

/** 탭 사이 직렬화(Web Locks). 없으면 이관을 시작하지 않는다(보수적으로) */
export interface MigrationLocks { request<T>(name: string, options: { mode: 'exclusive' }, callback: () => Promise<T>): Promise<T> }
export const MIGRATION_LOCK = 'dh-migration';

/** 이관 한 번 시도(위 순서). 탭 사이 잠금(dh-migration) 안에서 목록·작업 기록을 다시 읽고 끝까지 한다. 잠금이 없으면 'skip' */
export async function runOnlineMigration(kv: KV, accountId: string, d: OnlineMigrationDeps): Promise<OnlineMigrationResult> {
  if (!d.locks) return 'skip';
  return d.locks.request(MIGRATION_LOCK, { mode: 'exclusive' }, () => migrateLocked(kv, accountId, d));
}

async function migrateLocked(kv: KV, accountId: string, d: OnlineMigrationDeps): Promise<OnlineMigrationResult> {
  const loaded = loadRoster(kv);
  if (loaded === 'corrupt') return 'skip';
  let roster: Roster = loaded ?? freshRoster(accountId);
  if (roster.migrated) return 'skip';
  const st = await d.api.getMigration();
  if (st.done) { clearStaging(kv, null); return adopt(kv, roster, accountId, d, st, 'adopt'); }
  // 이관 작업 기록: 있으면 그 계획·스냅샷(claim별 대기 키) 그대로, 없으면 지금 원래 진행으로 한 번 만들어 고정한다
  let job: PendingMigration | null = roster.pendingMigration?.account === accountId ? roster.pendingMigration : null;
  if (job && !job.ids.every((id) => readStaged(kv, job!.claim, id)?.[HERO_KEY] !== undefined)) job = null; // 대기 스냅샷이 사라졌으면 다시
  if (!job) {
    const plan = planMigration(kv, accountId);
    if (!plan) {
      if (st.allocation) return 'busy'; // 다른 기기가 이관 중 — 끝나면 받는다
      return adopt(kv, roster, accountId, d, { allocation: null, hash: null }, 'none');
    }
    const claim = (d.randomHex ?? hex)(16);
    try {
      for (const c of plan.characters) {
        const text = JSON.stringify(c.keys);
        kv.setItem(stagingKey(claim, c.id), text);
        if (kv.getItem(stagingKey(claim, c.id)) !== text) throw new Error('verify');
      }
    } catch { clearStaging(kv, null); throw new TransportError(0, 'storage'); }
    job = {
      account: accountId, claim, hash: plan.hash, allocation: plan.allocation, higher: plan.higher, legacyHash: legacyHashOf(kv),
      ids: plan.characters.map((c) => c.id),
      origins: Object.fromEntries(plan.characters.map((c) => [c.id, c.origin])),
      classes: Object.fromEntries(plan.characters.map((c) => [c.id, c.cls])),
    };
    roster = { ...roster, pendingMigration: job };
    if (!saveRoster(kv, roster)) throw new TransportError(0, 'storage');
    clearStaging(kv, claim);
  }
  const res = await d.api.claimMigration(job.allocation, job.hash, job.claim);
  if (res.done) { clearStaging(kv, null); return adopt(kv, roster, accountId, d, res, 'adopt'); }
  if (!res.mine || !Number.isSafeInteger(res.epoch)) return 'busy';
  const epoch = res.epoch!;
  try {
    const now = (d.now ?? Date.now)();
    const staged: Record<string, Record<string, string>> = {};
    const uploaded: Record<string, { rev: number; snap: Snapshot }> = {};
    for (const id of job.ids) {
      const keys = readStaged(kv, job.claim, id);
      if (!keys) throw new TransportError(0, 'storage');
      staged[id] = keys;
      await d.api.create({ id, cls: job.classes[id], look: 0, name: null, origin: job.origins[id], claim: job.claim, epoch });
      // 업로드 스냅샷 = 대기 키의 진행 키(사본 제외). 펜싱 토큰 — 일반 동기화의 rev 충돌 재시도를 타지 않는다. 같은 계획·같은 id면 덮어쓰기(멱등)
      const snap = collectSnapshot({ getItem: (k) => keys[k] ?? null, setItem: () => {}, removeItem: () => {} })!;
      const up = await d.api.uploadMigrationSave(id, snap, job.claim, epoch);
      uploaded[id] = { rev: up.rev, snap };
      await d.api.renewMigration(job.claim, epoch);
    }
    // 완료 전에 로컬 설치·검증: 대기 스냅샷을 캐릭터 키로 옮기고(계획 밖 사본은 지움) 다시 읽어 해시가 같을 때만 기준을 적는다.
    // 하나라도 실패하면 완료하지 않는다(서버 이관은 미완 · 대기 키·작업 기록 그대로 — 다음 시도가 같은 claim으로 이어서 다시 설치)
    for (const id of job.ids) {
      const plan = { characters: [{ id, cls: job.classes[id], origin: job.origins[id], level: 0, keys: staged[id] }], higher: job.higher, allocation: job.allocation, hash: job.hash };
      const ok = installVerified(kv, id, accountId, uploaded[id].rev, uploaded[id].snap, () => { if (writeMigration(kv, plan).kind !== 'migrated') throw new Error('write'); }, now);
      if (!ok) throw new TransportError(0, 'storage');
    }
    await d.api.finishMigration(job.claim, epoch, job.hash);
    const list = await d.api.list();
    // 이 작업 밖의 캐릭터(먼저 병합된 게스트 캐릭터·다른 기기에서 만든 캐릭터)도 받아 설치·검증한다 — 실패하면 「받는 중」(선택 불가)
    const pending = new Set<string>();
    for (const c of list.characters) {
      if (job.ids.includes(c.id) || c.deletedAt) continue;
      try {
        const sv = await d.transport(c.id).getSave(false);
        const data = sv.data ?? EMPTY_SERVER_SNAPSHOT;
        if (!installVerified(kv, c.id, accountId, sv.rev, data, () => writeSnapshot(new CharacterStorage(kv, c.id), data), now)) pending.add(c.id);
      } catch { pending.add(c.id); }
    }
    const out: Roster = {
      ...(loadRoster(kv) as Roster | null ?? roster),
      characters: list.characters.map((c) => fromView(c, accountId)), // 받기 실패(pending)는 기준이 없어 「받는 중」으로 파생된다
      current: job.higher,
      migrated: { v: MIGRATION_VERSION, at: now, mode: 'first', account: accountId, hash: job.hash, allocation: job.allocation, ids: job.ids, higher: job.higher, legacyHash: job.legacyHash },
    };
    delete out.pendingMigration;
    if (!saveRoster(kv, out)) throw new TransportError(0, 'storage');
    clearStaging(kv, null);
    return 'done';
  } catch (err) {
    // 소유를 잃었다(임대가 끝나 다른 기기가 넘겨받음 — 펜싱): 작업 기록을 버리고 다음 동기화에 받기로
    if (err instanceof TransportError && err.code === 'migration-lost') {
      const cur = loadRoster(kv);
      if (cur && cur !== 'corrupt') { const { pendingMigration: _p, ...rest } = cur; saveRoster(kv, rest); }
      clearStaging(kv, null);
      return 'busy';
    }
    throw err;
  }
}

/** 브라우저 Web Locks(없으면 null — 이관하지 않는다) */
export function browserLocks(): MigrationLocks | null {
  try {
    const l = typeof navigator !== 'undefined' ? (navigator as unknown as { locks?: MigrationLocks }).locks : undefined;
    return l && typeof l.request === 'function' ? l : null;
  } catch { return null; }
}
/** 같은 실행 안의 직렬화 잠금(시험·두 탭 흉내 — 이름마다 하나씩 차례로) */
export function memoryLocks(): MigrationLocks {
  const tails = new Map<string, Promise<unknown>>();
  return {
    request<T>(name: string, _o: { mode: 'exclusive' }, cb: () => Promise<T>): Promise<T> {
      const prev = tails.get(name) ?? Promise.resolve();
      const run = prev.then(cb, cb);
      tails.set(name, run.catch(() => undefined));
      return run;
    },
  };
}
